import React, { useEffect, useState } from "react";
import {
  getAssessmentDetail,
  getPropertyDetail,
  getUploads,
} from "../services/apiService";
import { getClientEntry, getMediaBaseUrl } from "../config/clientConfig";
import { getSourceName } from "../services/authService";
import { buildMediaUrl } from "../utils/mediaUrl";
import {
  pickAssessmentImage,
  pickPropertyDataImage,
  placeholderFor,
  PROPERTY_SIDES,
} from "../utils/caseThumbnail";

/**
 * A row's thumbnail, for the clients whose case list does not carry one.
 *
 * Two of them do not, for different reasons:
 *
 *   property   assessment_list/all returns car_images: {} and no image path
 *              anywhere else; the photographs are only in GET /vehicle.
 *   hegic      its v2 list returns id, ref_num, claim_state and created_at —
 *              that is the whole row. The images are in GET /assessment.
 *
 * So the thumbnail fetches for itself, which is a request per row. Three
 * things keep that affordable, and they are the reason this is one component
 * rather than one per client: resolved paths are cached per ref_num for the
 * session, concurrent rows asking for the same case share a request, and at
 * most a few run at once.
 *
 * Which call to make is declared by the client (`listThumbnail`), not guessed
 * from the line — a client whose list starts carrying a cover image just drops
 * the key and pickSideImage takes over again.
 */

// ref_num -> media path, or null for "asked, and there is none".
const resolved = new Map();
const inFlight = new Map();

const MAX_CONCURRENT = 3;
let active = 0;
const waiting = [];

const runNext = () => {
  if (active >= MAX_CONCURRENT) return;
  const next = waiting.shift();
  if (!next) return;
  active += 1;
  next().finally(() => {
    active -= 1;
    runNext();
  });
};

const queue = (task) =>
  new Promise((resolve, reject) => {
    waiting.push(() => task().then(resolve, reject));
    runNext();
  });

const LOADERS = {
  vehicle: async (refNum) => {
    const sides =
      getClientEntry(getSourceName())?.listImageSides || PROPERTY_SIDES;
    const raw = await getPropertyDetail(refNum);
    return pickPropertyDataImage(raw?.property_data, sides);
  },
  assessment: async (refNum) => pickAssessmentImage(await getAssessmentDetail(refNum)),
  // The cheapest of the three: ~5 KB, against a whole assessment.
  uploads: async (refNum) => (await getUploads(refNum))[0]?.picture || null,
};

const loadThumb = (refNum, strategy) => {
  if (resolved.has(refNum)) return Promise.resolve(resolved.get(refNum));
  if (inFlight.has(refNum)) return inFlight.get(refNum);

  const load = LOADERS[strategy];
  if (!load) return Promise.resolve(null);

  const request = queue(() => load(refNum))
    .then((path) => {
      resolved.set(refNum, path || null);
      return path || null;
    })
    .catch(() => {
      // A thumbnail is not worth a retry storm; remember the miss.
      resolved.set(refNum, null);
      return null;
    })
    .finally(() => inFlight.delete(refNum));

  inFlight.set(refNum, request);
  return request;
};

const toUrl = (path) => {
  const client = getClientEntry(getSourceName());
  return buildMediaUrl(path, {
    baseUrl: getMediaBaseUrl(),
    pathPrefix: client?.mediaPathPrefix,
  });
};

export default function CaseThumb({ refNum, className, alt = "" }) {
  const client = getClientEntry(getSourceName());
  const strategy = client?.listThumbnail;
  const placeholder = placeholderFor(client?.inspectionLine);

  const [src, setSrc] = useState(() => {
    const known = refNum ? resolved.get(refNum) : null;
    return known ? toUrl(known) : "";
  });

  useEffect(() => {
    if (!refNum || !strategy || src) return undefined;
    let cancelled = false;

    loadThumb(refNum, strategy).then((path) => {
      if (!cancelled && path) setSrc(toUrl(path));
    });

    return () => {
      cancelled = true;
    };
    // src is deliberately not a dependency: once it is set there is nothing
    // left to load, and including it would re-run on every resolution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refNum, strategy]);

  return (
    <img
      src={src || placeholder}
      alt={alt}
      loading="lazy"
      className={className}
      onError={(event) => {
        event.currentTarget.src = placeholder;
      }}
    />
  );
}
